import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { NavLink } from 'react-router';

import { HeaderNavMeasure, navItemSx, NAV_ROW_GAP, NavMenuButton } from '@/components/molecules';
import { type NavItem } from '@/config/navigation';
import { FIT, fitMark } from '@/hooks/useHeaderFit';
import { useHeaderNavItems } from '@/hooks/useHeaderNavItems';

type Props = {
  /** The full primary nav. What renders inline is decided by `visibleCount`. */
  items: readonly NavItem[];
  /** How many of them fit, from `useHeaderFit`. The rest fold into "More". */
  visibleCount: number;
  /** Text colour for the bar's current state: ink when solid, white while floating. */
  ink: string;
  /** False only while the bar floats over the dark home hero. */
  solid: boolean;
};

/**
 * The desktop navigation row: the items that fit, a "More" pill for any that
 * do not, and the hidden twin the fit is measured from.
 *
 * Extracted from `SiteHeader` when the row learned to fold. The header's job
 * is the arrangement of four stacked rows; deciding which nav items are on one
 * of them is a different job, and leaving both in one file is how that file
 * grows past the point where either is easy to find. `SiteHeader` now hands
 * this a number and gets a row back.
 *
 * It holds no state and runs no measurement — `useHeaderFit` lives in
 * `SiteHeader` because the same plan also governs `HeaderActions`, and two
 * components measuring the same row to reach the same conclusion is how the
 * two ends of a bar end up disagreeing about how much room there is.
 *
 * `position: relative` is load-bearing: `HeaderNavMeasure` is absolutely
 * positioned and has to be positioned against this row, not against the page.
 */
export function HeaderNav({ items, visibleCount, ink, solid }: Props) {
  const { inline, overflowMenu } = useHeaderNavItems(items, visibleCount);

  return (
    <Box
      component="nav"
      aria-label="Main"
      {...fitMark(FIT.nav)}
      sx={{
        position: 'relative',
        display: { xs: 'none', lg: 'flex' },
        alignItems: 'center',
        gap: NAV_ROW_GAP,
        mx: 'auto',
        minWidth: 0,
      }}
    >
      {inline.map((item) =>
        item.kind === 'link' ? (
          <Button key={item.to} component={NavLink} to={item.to} end sx={navItemSx(ink, solid)}>
            {item.label}
          </Button>
        ) : (
          <NavMenuButton key={item.label} menu={item} color={ink} solid={solid} />
        ),
      )}
      {overflowMenu && <NavMenuButton menu={overflowMenu} color={ink} solid={solid} />}
      <HeaderNavMeasure items={items} />
    </Box>
  );
}
